import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import {
    NewSlideshow,
    NewSlideshowSlide,
    Slideshow,
    SlideshowSlide,
    SlideshowSlideValidation,
    UnitySlideshow,
    UnitySlideshowSlide,
} from '@@api/services/slideshow/schemas';
import {
    deserializeSlideshow,
    deserializeSlideshowSlide,
    serializeNewSlideshow,
    serializeSlideshow,
    serializeSlideshowSlide,
} from '@@api/services/slideshow/transformers';
import { PaginationParams } from '@@api/utils/schemas/queryParams';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';

export const slideshowService = defineService(
    (defineEndpoint) => ({
        slideshow: {
            get: defineEndpoint({
                method: 'GET',
                path: '/slideshows/:id',
                pathParams: z.object({
                    id: Slideshow.shape.id,
                }),
                responses: {
                    200: UnitySlideshow.transform(deserializeSlideshow),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/slideshows/:id',
                pathParams: z.object({
                    id: Slideshow.shape.id,
                }),
                body: Slideshow.transform(serializeSlideshow),
                responses: {
                    200: UnitySlideshow.transform(deserializeSlideshow),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/slideshows',
                body: NewSlideshow.transform(serializeNewSlideshow),
                responses: {
                    201: UnitySlideshow.transform(deserializeSlideshow),
                },
            }),
            duplicate: defineEndpoint({
                method: 'POST',
                path: '/slideshows',
                query: z.object({
                    sourceId: Slideshow.shape.id,
                }),
                body: z.null(),
                responses: {
                    201: UnitySlideshow.transform(deserializeSlideshow),
                },
            }),
        },
        slide: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/slideshows/:id/slides',
                pathParams: z.object({
                    id: Slideshow.shape.id,
                }),
                query: z.object({
                    ...PaginationParams.pick({ size: true, page: true }).shape,
                }),
                responses: {
                    200: paginated(UnitySlideshowSlide.transform(deserializeSlideshowSlide)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/slideshows/:slideshowId/slides/:slideId',
                pathParams: z.object({
                    slideshowId: Slideshow.shape.id,
                    slideId: SlideshowSlide.shape.id,
                }),
                responses: {
                    200: UnitySlideshowSlide.transform(deserializeSlideshowSlide),
                },
            }),
            patch: defineEndpoint({
                method: 'PATCH',
                path: '/slideshows/:slideshowId/slides/:slideId',
                pathParams: z.object({
                    slideshowId: z.string().uuid(),
                    slideId: z.number(),
                }),
                body: SlideshowSlide.pick({ variants: true })
                    .optional()
                    // @ts-expect-error Need to improve types here
                    .transform(serializeSlideshowSlide),
                responses: {
                    200: UnitySlideshowSlide.transform(deserializeSlideshowSlide),
                },
            }),
            patchAll: defineEndpoint({
                method: 'PATCH',
                path: '/slideshows/:id/slides',
                pathParams: z.object({
                    id: z.string().uuid(),
                }),
                body: z.union([
                    z.array(
                        z.object({
                            op: z.literal('move'),
                            from: z.string(),
                            path: z.string(),
                        }),
                    ),
                    SlideshowSlide.pick({ variants: true })
                        .optional()
                        // @ts-expect-error Need to improve types here
                        .transform(serializeSlideshowSlide),
                ]),
                responses: {
                    200: z.never(),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/slideshows/:slideshowId/slides/:slideId',
                pathParams: z.object({
                    slideshowId: Slideshow.shape.id,
                    slideId: z.number(),
                }),
                body: SlideshowSlide.transform(serializeSlideshowSlide),
                responses: {
                    200: UnitySlideshowSlide.transform(deserializeSlideshowSlide),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/slideshows/:id/slides',
                pathParams: z.object({
                    id: Slideshow.shape.id,
                }),
                body: NewSlideshowSlide.transform(serializeSlideshowSlide),
                responses: {
                    201: UnitySlideshowSlide.transform(deserializeSlideshowSlide),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/slideshows/:slideshowId/slides/:slideId',
                pathParams: z.object({
                    slideshowId: Slideshow.shape.id,
                    slideId: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
            validateAll: defineEndpoint({
                method: 'GET',
                path: '/slideshows/:id/slides/valid',
                pathParams: z.object({
                    id: Slideshow.shape.id,
                }),
                query: z.object({
                    includeDetails: z.boolean(),
                }),
                responses: {
                    200: z.array(SlideshowSlideValidation),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.slideshowUrl,
    },
);
